// Payment Links Configuration - UPDATED VERSION
// Updated: January 2025
// Stage 1: Card capture model with Pay Now button fallback
// Auto-switches between test and live URLs based on environment
// Appends client_reference_id for webhook tracking

// ============================================
// ENVIRONMENT DETECTION
// ============================================
const getEnvironment = () => {
  if (typeof window === 'undefined') return 'production';
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'development';
  }
  if (hostname.includes('planaidesign-dev')) {
    return 'development';
  }
  return 'production';
};

const isDevelopment = getEnvironment() === 'development';

// ============================================
// STAGE 1 PAYMENT LINKS (Test & Live)
// ============================================
const stage1Links = {
  'single': {
    test: 'https://buy.stripe.com/test_aFa28kcFkbAc77r7885wI02',
    live: 'https://buy.stripe.com/bJedR29t8fQs63n5005wI1k'
  },
  'duplex': {
    test: 'https://buy.stripe.com/test_28E3co8p447KajDfEE5wI03',
    live: 'https://buy.stripe.com/fZu7sE34K0Vy63n2RS5wI1r'
  },
  'townhouse-3-5': {
    test: 'https://buy.stripe.com/test_7sYfZa48ObAc2RbgII5wI04',
    live: 'https://buy.stripe.com/4gMfZaaxceMo3VfeAA5wI1s'
  },
  'townhouse-6-9': {
    test: 'https://buy.stripe.com/test_aFa8wI7l0dIkezT8cc5wI05',
    live: 'https://buy.stripe.com/28E3cocFk47K2Rb6445wI1t'
  },
  'secondary': {
    test: 'https://buy.stripe.com/test_aFafZa8p48o0dvPboo5wI06',
    live: 'https://buy.stripe.com/aFa14g8p4aw863ngII5wI1n'
  },
  'minor-renovation': {
    test: 'https://buy.stripe.com/test_6oU6oA8p4gUw77r8cc5wI07',
    live: 'https://buy.stripe.com/00wfZaaxc5bO2Rb7885wI1m'
  },
  'major-renovation': {
    test: 'https://buy.stripe.com/test_00w6oAaxc7jWezT5005wI00',
    live: 'https://buy.stripe.com/dRm4gs48OfQsfDX1NO5wI1q'
  }
};

// ============================================
// STAGE 1 PRICING (in AUD)
// ============================================
export const stage1Pricing = {
  'single': 199,
  'duplex': 249,
  'townhouse': 499,        // Default for townhouse (3-5 units)
  'townhouse-3-5': 499,
  'townhouse-6-9': 799,
  'secondary': 149,
  'minor-renovation': 149,
  'major-renovation': 199,
};

// Helper to get Stage 1 price
export const getStage1Price = (projectType, townhouseUnits = null) => {
  const normalizedType = projectType?.toLowerCase()?.trim();
  
  // Handle townhouse with unit count
  if (normalizedType === 'townhouse' && townhouseUnits) {
    const units = String(townhouseUnits);
    if (units === '6-9' || parseInt(units) >= 6) {
      return stage1Pricing['townhouse-6-9'];
    }
    return stage1Pricing['townhouse-3-5'];
  }
  
  return stage1Pricing[normalizedType] || null;
};

// ============================================
// HELPER: Append projectId to Payment Link
// ============================================
const appendProjectId = (baseUrl, projectId) => {
  if (!projectId) return baseUrl;
  
  // Use client_reference_id parameter - Stripe passes this to webhooks
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}client_reference_id=${projectId}`;
};

// ============================================
// MAIN PAYMENT LINK FUNCTION
// ============================================
export const getPaymentLink = (stage, projectType, paymentType, projectDetails = {}) => {
  const normalizedType = projectType?.toLowerCase()?.trim();
  const projectId = projectDetails.projectId || projectDetails.firebaseId || null;
  
  console.log('Getting payment link for:', { 
    stage, 
    originalType: projectType,
    normalizedType,
    paymentType, 
    projectId,
    projectDetails,
    environment: isDevelopment ? 'development' : 'production'
  });
  
  // ============================================
  // STAGE 1 - Single Payment Links
  // ============================================
  if (stage === 1 || stage === '1') {
    let linkKey = normalizedType;
    
    // Handle townhouse with unit count
    if (normalizedType === 'townhouse') {
      const units = projectDetails.townhouseUnits || '3-5';
      if (units === '6-9' || parseInt(units) >= 6) {
        linkKey = 'townhouse-6-9';
      } else {
        linkKey = 'townhouse-3-5';
      }
    }
    
    const linkConfig = stage1Links[linkKey];
    
    if (linkConfig) {
      const baseLink = isDevelopment ? linkConfig.test : linkConfig.live;
      const link = appendProjectId(baseLink, projectId);
      console.log(`Found Stage 1 payment link (${isDevelopment ? 'test' : 'live'}):`, link);
      return link;
    } else {
      console.error(`Stage 1: Payment configuration not found for type: ${linkKey}`);
      console.error('Available types:', Object.keys(stage1Links));
      return null;
    }
  }
  
  // ============================================
  // STAGE 2 - Check for bundled vs standard
  // ============================================
  if (stage === 2 || stage === '2') {
    const typeKey = normalizedType;
    
    // Check if this is a bundled Stage 2 project (includes Stage 1)
    const isBundled = projectDetails.includesSiteInvestigation || 
                     projectDetails.hasCompletedStage1 === false;
    
    console.log('Stage 2 payment - Type:', typeKey, 'Bundled:', isBundled);
    
    // Stage 2 BUNDLED payment links (includes Stage 1 cost)
    const stage2BundledLinks = {
      'single': {
        '1': { // 1 storey - Total: $598 ($199 Stage 1 + $399 Stage 2) - 3 payments
          'initial': 'https://buy.stripe.com/00w00cbBg7jWfDXcss5wI0I',    // $150
          'progress': 'https://buy.stripe.com/bJeeV6bBg9s4bnH1NO5wI0J',   // $224
          'final': 'https://buy.stripe.com/9B6cMYgVAgUwajDeAA5wI0K'       // $224
        },
        '2': { // 2 storeys - Total: $749 ($199 + $550) - 3 payments
          'initial': 'https://buy.stripe.com/14A00c8p40VycrL8cc5wI0L',    // $150
          'progress': 'https://buy.stripe.com/aFa6oA48O33GajDdww5wI0M',   // $299.50
          'final': 'https://buy.stripe.com/8x2dR2axcbAcajD0JK5wI0N'       // $299.50
        },
        '3': { // 3 storeys - Total: $849 ($199 + $650) - 3 payments
          'initial': 'https://buy.stripe.com/bJe9AM9t8aw82Rb3VW5wI0O',    // $150
          'progress': 'https://buy.stripe.com/00wbIUaxc9s4fDX0JK5wI0P',   // $349.50
          'final': 'https://buy.stripe.com/6oU00ccFkfQs8bv0JK5wI0Q'       // $349.50
        }
      },
      'duplex': {
        '1': { // 1 storey - Total: $598 - 3 payments
          'initial': 'https://buy.stripe.com/dRmfZabBg0VycrLeAA5wI0R',    // $150
          'progress': 'https://buy.stripe.com/00w7sE5cSeMo8bvgII5wI0S',   // $224
          'final': 'https://buy.stripe.com/cNi8wI9t8dIk2RbfEE5wI0T'       // $224
        },
        '2': { // 2 storeys - Total: $749 - 3 payments
          'initial': 'https://buy.stripe.com/4gM3co9t88o09fzakk5wI0U',    // $150
          'progress': 'https://buy.stripe.com/9B614g20GgUwbnHfEE5wI0V',   // $299.50
          'final': 'https://buy.stripe.com/cNi00c5cS1ZCezTeAA5wI0W'       // $299.50
        },
        '3': { // 3 storeys - Total: $849 - 3 payments
          'initial': 'https://buy.stripe.com/dRm5kweNseMo3Vfboo5wI0X',    // $150
          'progress': 'https://buy.stripe.com/aFa7sE48O0VycrL7885wI0Y',   // $349.50
          'final': 'https://buy.stripe.com/3cIeV6dJo5bObnHakk5wI0Z'       // $349.50
        }
      },
      'townhouse': {
        '3-5': { // Total: $1,048 ($249 + $799) - 3 payments
          'initial': 'https://buy.stripe.com/28E5kwdJo1ZC2Rb7885wI10',    // $150
          'progress': 'https://buy.stripe.com/4gM6oA34Kaw8dvP9gg5wI11',   // $449
          'final': 'https://buy.stripe.com/bJe5kw8p49s49fz8cc5wI12'       // $449
        },
        '6-9': { // Total: $1,348 ($249 + $1,099) - 3 payments
          'initial': 'https://buy.stripe.com/14AbIU8p45bOajD2RS5wI13',    // $150
          'progress': 'https://buy.stripe.com/14A4gs5cSbAccrLboo5wI14',   // $599
          'final': 'https://buy.stripe.com/aFaaEQ34K33GfDX7885wI15'       // $599
        }
      },
      'secondary': { // Total: $448 ($149 + $299) - Under $500, stays 2 payments
        'initial': 'https://buy.stripe.com/28E4gsaxcgUw3VffEE5wI16',      // $150
        'balance': 'https://buy.stripe.com/3cI7sE34KcEg63ncss5wI17'       // $298
      },
      'minor-renovation': { // Total: $448 - Under $500, stays 2 payments
        'initial': 'https://buy.stripe.com/cNi00cfRw8o0gI19gg5wI18',      // $150
        'balance': 'https://buy.stripe.com/dRm8wIbBgdIk8bveAA5wI1a'       // $298
      },
      'major-renovation': {
        '1': { // 1 storey - Total: $548 ($199 + $349) - 3 payments
          'initial': 'https://buy.stripe.com/28EbIU48O8o077r6445wI1b',    // $150
          'progress': 'https://buy.stripe.com/28EaEQ5cS47K0J3boo5wI1c',   // $199
          'final': 'https://buy.stripe.com/bJe6oA8p4aw88bv6445wI1d'       // $199
        },
        '2': { // 2 storeys - Total: $648 ($199 + $449) - 3 payments
          'initial': 'https://buy.stripe.com/8x2aEQ20G6fS8bvdww5wI1e',    // $150
          'progress': 'https://buy.stripe.com/00w4gs34K1ZC77rfEE5wI1f',   // $249
          'final': 'https://buy.stripe.com/6oUaEQcFk0Vy1N70JK5wI1g'       // $249
        }
      }
    };
    
    // Stage 2 STANDARD payment links (Stage 2 only - existing links)
    const stage2Links = {
      'single': {
        '1': { // 1 storey
          'initial': 'https://buy.stripe.com/6oU28keNs9s463n9gg5wI0c',    // $150
          'balance': 'https://buy.stripe.com/aFa4gs48OdIkbnH9gg5wI0d'     // $249
        },
        '2': { // 2 storeys
          'initial': 'https://buy.stripe.com/3cIdR2cFk1ZC3Vfcss5wI0e',    // $150
          'progress': 'https://buy.stripe.com/7sY7sE7l033G77r9gg5wI0f',   // $200
          'final': 'https://buy.stripe.com/5kQbIUcFkbAc3Vf6445wI0g'       // $200
        },
        '3': { // 3 storeys
          'initial': 'https://buy.stripe.com/7sYfZa34K5bO63nakk5wI0h',    // $150
          'progress': 'https://buy.stripe.com/14AcMYfRw1ZC8bv5005wI0i',   // $250
          'final': 'https://buy.stripe.com/8x29AMfRwcEg63n3VW5wI0j'       // $250
        }
      },
      'duplex': {
        '1': { // 1 storey
          'initial': 'https://buy.stripe.com/cNi9AMbBgcEgdvPdww5wI0k',    // $150
          'balance': 'https://buy.stripe.com/4gMfZa8p49s4fDXdww5wI0l'     // $249
        },
        '2': { // 2 storeys
          'initial': 'https://buy.stripe.com/eVqfZa5cS5bOezTcss5wI0m',    // $150
          'progress': 'https://buy.stripe.com/dRm6oAbBgaw8dvPgII5wI0n',   // $200
          'final': 'https://buy.stripe.com/bJefZa6gW33GfDXgII5wI0o'       // $200
        },
        '3': { // 3 storeys
          'initial': 'https://buy.stripe.com/28EaEQ9t8cEgbnHdww5wI0p',    // $150
          'progress': 'https://buy.stripe.com/cNi6oA9t81ZCgI12RS5wI0q',   // $250
          'final': 'https://buy.stripe.com/dRm14ggVA9s4crLcss5wI0r'       // $250
        }
      },
      'townhouse': {
        '3-5': {
          'initial': 'https://buy.stripe.com/dRm14g7l0eMo8bvcss5wI0s',    // $150
          'progress': 'https://buy.stripe.com/bJecMYeNs33GdvPfEE5wI0t',   // $324.50
          'final': 'https://buy.stripe.com/28E00c34K6fS63n0JK5wI0u'       // $324.50
        },
        '6-9': {
          'initial': 'https://buy.stripe.com/5kQ9AMfRw0VygI1akk5wI0v',    // $150
          'progress': 'https://buy.stripe.com/eVqaEQaxcgUwfDX9gg5wI0w',   // $474.50
          'final': 'https://buy.stripe.com/dRm5kw5cSdIkdvPdww5wI0x'       // $474.50
        }
      },
      'secondary': {
        'initial': 'https://buy.stripe.com/14AbIU6gWfQs0J3fEE5wI0y',      // $150
        'balance': 'https://buy.stripe.com/cNi5kwfRweMocrLgII5wI0z'       // $149
      },
      'minor-renovation': {
        'initial': 'https://buy.stripe.com/eVqdR220G8o02RbgII5wI0A',      // $150
        'balance': 'https://buy.stripe.com/eVq5kw9t8cEg1N70JK5wI0B'       // $149
      },
      'major-renovation': {
        '1': { // 1 storey
          'initial': 'https://buy.stripe.com/4gMeV6bBg5bO8bv8cc5wI0C',    // $150
          'balance': 'https://buy.stripe.com/dRmdR248Oaw877rboo5wI0D'     // $199
        },
        '2': { // 2 storeys
          'initial': 'https://buy.stripe.com/6oU4gscFk33G63n3VW5wI0E',    // $150
          'balance': 'https://buy.stripe.com/14A4gs48OgUw63ncss5wI0F'     // $299
        }
      }
    };
    
    // Select the appropriate link set based on bundled status
    const linkSet = isBundled ? stage2BundledLinks : stage2Links;
    
    // Determine payment type based on payment number
    const paymentsMade = projectDetails.paymentsMade || 0;
    const totalPayments = projectDetails.totalPayments || 2;
    
    let actualPaymentType = paymentType;
    if (!paymentType) {
      // Auto-determine payment type
      if (paymentsMade === 0) {
        actualPaymentType = 'initial';
      } else if (paymentsMade === 1 && totalPayments === 2) {
        actualPaymentType = 'balance';
      } else if (paymentsMade === 1 && totalPayments === 3) {
        actualPaymentType = 'progress';
      } else if (paymentsMade === 2) {
        actualPaymentType = 'final';
      }
    }
    
    console.log('Payment type determined:', actualPaymentType);
    console.log('Using link set:', isBundled ? 'bundled' : 'standard');
    console.log('Looking for:', typeKey, 'in', Object.keys(linkSet));
    
    // Get the correct link based on type and variation
    let baseLink = null;
    
    if (typeKey === 'single' || typeKey === 'duplex') {
      const storeys = String(projectDetails.storeys || 1);
      console.log('Storeys:', storeys);
      if (linkSet[typeKey] && linkSet[typeKey][storeys]) {
        if (linkSet[typeKey][storeys][actualPaymentType]) {
          baseLink = linkSet[typeKey][storeys][actualPaymentType];
        } else {
          console.error(`Payment type ${actualPaymentType} not found for ${typeKey} ${storeys} storeys`);
        }
      } else {
        console.error(`No configuration for ${typeKey} with ${storeys} storeys`);
      }
    } else if (typeKey === 'townhouse') {
      const units = projectDetails.townhouseUnits || '3-5';
      console.log('Units:', units);
      if (linkSet[typeKey] && linkSet[typeKey][units]) {
        if (linkSet[typeKey][units][actualPaymentType]) {
          baseLink = linkSet[typeKey][units][actualPaymentType];
        } else {
          console.error(`Payment type ${actualPaymentType} not found for ${typeKey} ${units} units`);
        }
      } else {
        console.error(`No configuration for ${typeKey} with ${units} units`);
      }
    } else if (typeKey === 'major-renovation') {
      const storeys = String(projectDetails.storeys || 1);
      console.log('Major renovation storeys:', storeys);
      if (linkSet[typeKey] && linkSet[typeKey][storeys]) {
        if (linkSet[typeKey][storeys][actualPaymentType]) {
          baseLink = linkSet[typeKey][storeys][actualPaymentType];
        } else {
          console.error(`Payment type ${actualPaymentType} not found for ${typeKey} ${storeys} storeys`);
        }
      } else {
        console.error(`No configuration for ${typeKey} with ${storeys} storeys`);
      }
    } else {
      // Secondary and minor-renovation
      if (linkSet[typeKey]) {
        if (linkSet[typeKey][actualPaymentType]) {
          baseLink = linkSet[typeKey][actualPaymentType];
        } else {
          console.error(`Payment type ${actualPaymentType} not found for ${typeKey}`);
        }
      } else {
        console.error(`No configuration for ${typeKey}`);
      }
    }
    
    if (baseLink) {
      const link = appendProjectId(baseLink, projectId);
      console.log('Found Stage 2 payment link:', link);
      return link;
    }
    
    console.error(`Stage 2 payment link not found for ${typeKey} - ${actualPaymentType} (bundled: ${isBundled})`);
    console.error('Available types in link set:', Object.keys(linkSet));
    return null;
  }
  
  // Stage 3 & 4 are quote-based, no fixed payment links
  if (stage >= 3) {
    console.log('Stage 3 & 4 use dynamic quotes, no fixed payment links');
    return null;
  }
  
  console.error(`No payment link found for stage ${stage}, type ${normalizedType}, payment ${paymentType}`);
  return null;
};

// Get revision payment links for Stage 2
export const getRevisionPaymentLink = (stage, fee, projectId = null) => {
  console.log('Getting revision payment link for stage:', stage, 'fee:', fee);
  
  const revisionLinks = {
    stage2: {
      99: 'https://buy.stripe.com/14AeV60WC5bO1N71NO5wI0G',   // $99 revision
      150: 'https://buy.stripe.com/bJe8wI7l047KdvPakk5wI1j'  // $150 revision
    }
  };
  
  const stageKey = `stage${stage}`;
  if (revisionLinks[stageKey] && revisionLinks[stageKey][fee]) {
    const baseLink = revisionLinks[stageKey][fee];
    const link = appendProjectId(baseLink, projectId);
    console.log('Found revision payment link:', link);
    return link;
  }
  
  console.error(`Revision payment link not found for stage ${stage}, fee ${fee}`);
  console.error('Available fees for', stageKey, ':', revisionLinks[stageKey] ? Object.keys(revisionLinks[stageKey]) : 'none');
  return null;
};

// Export default
export default { getPaymentLink, getRevisionPaymentLink, getStage1Price, stage1Pricing };